import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Audit overlay activated by URL query ?audit=1
 * Floating panel showing live state for ChatGPT / external auditors.
 * Strictly preview-only: requires backend /api/debug/state to succeed (which itself is gated).
 */
export default function AuditOverlay() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const active = params.get("audit") === "1";
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(true);
  const [error, setError] = useState(null);
  const [apiCalls, setApiCalls] = useState([]);
  const interceptorRef = useRef(null);

  // Capture API timings via axios interceptor (one-time)
  useEffect(() => {
    if (!active || interceptorRef.current) return;
    interceptorRef.current = api.interceptors.request.use((cfg) => {
      cfg.metadata = { start: performance.now() };
      return cfg;
    });
    const respId = api.interceptors.response.use(
      (resp) => {
        const start = resp.config.metadata?.start;
        if (start) {
          const dur = Math.round(performance.now() - start);
          setApiCalls((arr) => [
            { url: resp.config.url, method: resp.config.method?.toUpperCase(), ms: dur, status: resp.status, t: new Date().toLocaleTimeString("fr-FR") },
            ...arr.slice(0, 14),
          ]);
        }
        return resp;
      },
      (err) => {
        const start = err.config?.metadata?.start;
        if (start) {
          const dur = Math.round(performance.now() - start);
          setApiCalls((arr) => [
            { url: err.config.url, method: err.config.method?.toUpperCase(), ms: dur, status: err.response?.status || "ERR", t: new Date().toLocaleTimeString("fr-FR") },
            ...arr.slice(0, 14),
          ]);
        }
        return Promise.reject(err);
      }
    );
    return () => {
      api.interceptors.response.eject(respId);
    };
  }, [active]);

  // Fetch debug/state on activation and on route change
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/debug/state");
        if (!cancelled) { setState(res.data); setError(null); }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Audit indisponible (preview only)");
      }
    })();
    return () => { cancelled = true; };
  }, [active, location.pathname]);

  if (!active) return null;

  return (
    <div
      data-testid="audit-overlay"
      style={{
        position: "fixed",
        bottom: 12,
        right: 12,
        zIndex: 99999,
        width: open ? 360 : 44,
        maxHeight: open ? "70vh" : 44,
        background: "#0F172A",
        color: "#E2E8F0",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 11,
        borderRadius: 10,
        boxShadow: "0 12px 30px rgba(0,0,0,0.35)",
        overflow: "hidden",
        border: "1px solid #1E293B",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="audit-toggle"
        style={{
          width: "100%", padding: open ? "8px 10px" : 0, height: open ? "auto" : 44,
          background: "linear-gradient(90deg, #1E40AF, #6366F1)", color: "#fff",
          border: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between",
          fontWeight: 800, letterSpacing: 0.5, textAlign: "left",
        }}
      >
        <span>{open ? "🔍 AUDIT MODE" : "🔍"}</span>
        {open && <span style={{ fontSize: 10, opacity: 0.85 }}>{location.pathname}</span>}
      </button>

      {open && (
        <div style={{ overflowY: "auto", maxHeight: "calc(70vh - 40px)", padding: 10 }}>
          {error && (
            <div style={{ background: "#7F1D1D", color: "#FECACA", padding: 8, borderRadius: 6, marginBottom: 8 }}>
              ⚠ {error}
            </div>
          )}

          {state && (
            <>
              <Section title="USER">
                <KV k="user_id" v={state.user.user_id} />
                <KV k="email" v={state.user.email} />
                <KV k="name" v={state.user.name} />
              </Section>

              <Section title="ROUTE">
                <KV k="pathname" v={location.pathname} />
                <KV k="search" v={location.search || "(none)"} />
              </Section>

              <Section title="MODE BUSINESS">
                <KV k="code" v={state.mode?.code} highlight />
                <KV k="label" v={state.mode?.label} />
                <KV k="reason" v={state.mode?.reason} />
              </Section>

              <Section title="KPI CA YTD">
                <KV k="réalisé" v={`${state.kpi_central?.ca_realise_ytd}€`} />
                <KV k="attendu" v={`${state.kpi_central?.ca_attendu_date}€`} />
                <KV k="delta_pct" v={`${state.kpi_central?.delta_pct}%`} />
                <KV k="status" v={state.kpi_central?.status?.label} />
              </Section>

              <Section title={`PLAN DU JOUR (${state.plan_du_jour.summary.done}/${state.plan_du_jour.summary.total})`}>
                {state.plan_du_jour.actions.slice(0, 8).map((a, i) => (
                  <div key={a.id} style={{ marginBottom: 4, paddingBottom: 4, borderBottom: "1px dashed #1E293B" }}>
                    <div style={{ color: "#FBBF24", fontSize: 10 }}>{i + 1}. [{a.type}] {a.completed ? "✓ " : ""}{a.snoozed ? "⏰ " : ""}</div>
                    <div style={{ color: "#E2E8F0", fontWeight: 700 }}>{a.title}</div>
                    <div style={{ color: "#94A3B8", fontStyle: "italic" }}>→ {a.reason}</div>
                  </div>
                ))}
              </Section>

              <Section title={`TOP 5 SCORES`}>
                {state.top5.map((d, i) => (
                  <div key={d.id} style={{ marginBottom: 4, paddingBottom: 4, borderBottom: "1px dashed #1E293B" }}>
                    <div style={{ color: "#A7F3D0" }}>{i + 1}. {d.nom?.slice(0, 38)} <b style={{ color: "#FBBF24" }}>{d.score}</b></div>
                    <div style={{ color: "#94A3B8", fontSize: 10 }}>
                      stage:{d.score_breakdown?.stage} · ca:{d.score_breakdown?.ca} · ★:{d.score_breakdown?.stars} · mandat:{d.score_breakdown?.mandat} · retard:{d.score_breakdown?.retard} · silence:{d.score_breakdown?.silence} · focus:{d.score_breakdown?.focus} · stagn:{d.score_breakdown?.stagnation}
                    </div>
                  </div>
                ))}
              </Section>

              <Section title="TIMINGS BACKEND (ms)">
                {Object.entries(state.timings_ms).map(([k, v]) => <KV key={k} k={k} v={`${v} ms`} />)}
              </Section>

              <Section title={`API CALLS (${apiCalls.length})`}>
                {apiCalls.length === 0 && <div style={{ color: "#64748B" }}>aucun appel depuis activation</div>}
                {apiCalls.slice(0, 10).map((c, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 6, fontSize: 10 }}>
                    <span style={{ color: c.status >= 400 ? "#FCA5A5" : "#A7F3D0", flexShrink: 0 }}>
                      {c.method} {c.status}
                    </span>
                    <span style={{ color: "#94A3B8", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.url}</span>
                    <span style={{ color: "#FBBF24", flexShrink: 0 }}>{c.ms}ms</span>
                  </div>
                ))}
              </Section>

              <div style={{ marginTop: 8, padding: 6, background: "#1E293B", borderRadius: 6, fontSize: 10, color: "#94A3B8" }}>
                Endpoints debug : <b>/api/debug/state</b> · <b>/api/debug/routes</b> · <b>/api/debug/dossier/&#123;id&#125;</b>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: "#60A5FA", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}

function KV({ k, v, highlight }) {
  return (
    <div style={{ display: "flex", gap: 6, lineHeight: 1.5 }}>
      <span style={{ color: "#64748B", minWidth: 70 }}>{k}</span>
      <span style={{ color: highlight ? "#FBBF24" : "#E2E8F0", fontWeight: highlight ? 700 : 400, wordBreak: "break-word", flex: 1 }}>
        {String(v ?? "—")}
      </span>
    </div>
  );
}
